import { useCallback, useEffect, useState } from "react";
import { format, parseISO } from "date-fns";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { MapPin, Pencil } from "lucide-react";

const t = (iso) => (iso ? format(parseISO(iso), "h:mma") : "—");
const toLocalInput = (iso) => (iso ? format(parseISO(iso), "yyyy-MM-dd'T'HH:mm") : "");
const hrs = (h) => (h == null ? "—" : `${h} h`);

export function SiteBadge({ loc, label }) {
  if (!loc) return <span className="text-xs text-muted-foreground">{label}: no GPS</span>;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${loc.on_site ? "text-[hsl(140_60%_32%)]" : "text-destructive"}`}>
      <MapPin className="w-3 h-3" /> {label}: {loc.on_site ? "on site" : `${loc.metres >= 1000 ? `${(loc.metres / 1000).toFixed(1)} km` : `${loc.metres} m`} away`}
    </span>
  );
}

export const variance = (v) => (v == null ? "—" : `${v > 0 ? "+" : ""}${v} h`);

export default function CheckInPanel({ jobId }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ start: "", end: "" });

  const load = useCallback(() => {
    api.get(`/jobs/${jobId}/checkin`).then((r) => {
      setData(r.data);
      setForm({ start: toLocalInput(r.data.actual_start), end: toLocalInput(r.data.actual_end) });
    }).catch(() => {});
  }, [jobId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const payload = {};
    if (form.start) payload.actual_start = new Date(form.start).toISOString();
    if (form.end) payload.actual_end = new Date(form.end).toISOString();
    if (payload.actual_start && payload.actual_end && payload.actual_end <= payload.actual_start) return toast.error("Finish must be after start");
    try {
      await api.put(`/jobs/${jobId}`, payload);
      toast.success("Times updated");
      setEditing(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (!data) return null;
  const v = data.variance_hours;
  return (
    <div className="rounded-sm border border-border/60 p-4 space-y-3 text-sm" data-testid="checkin-panel">
      <div className="grid grid-cols-3 gap-3">
        <div><div className="text-xs text-muted-foreground">Planned</div><div data-testid="checkin-planned">{t(data.planned_start)} – {t(data.planned_end)} · {hrs(data.planned_hours)}</div></div>
        <div><div className="text-xs text-muted-foreground">Actual</div><div data-testid="checkin-actual">{t(data.actual_start)} – {t(data.actual_end)} · {hrs(data.actual_hours)}</div></div>
        <div><div className="text-xs text-muted-foreground">Variance</div>
          <div data-testid="checkin-variance" className={v > 0 ? "text-destructive font-medium" : v < 0 ? "text-[hsl(140_60%_32%)] font-medium" : ""}>{variance(v)}</div></div>
      </div>
      <div className="flex flex-wrap gap-4">
        <SiteBadge loc={data.start_location} label="Start" />
        <SiteBadge loc={data.end_location} label="Finish" />
        {data.start_late_minutes != null && data.start_late_minutes > 5 && <span className="text-xs text-destructive">Started {data.start_late_minutes} min late</span>}
      </div>
      {data.edits.length > 0 && (
        <div className="text-xs text-muted-foreground space-y-0.5" data-testid="checkin-edits">
          {data.edits.map((e, i) => (
            <div key={i}>Edited {e.field === "actual_start" ? "start" : "finish"} {t(e.old)} → {t(e.new)} by {e.by} on {format(parseISO(e.at), "d MMM, h:mma")}</div>
          ))}
        </div>
      )}
      {editing ? (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-muted-foreground">Actual start<Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="checkin-edit-start" /></label>
          <label className="text-xs text-muted-foreground">Actual finish<Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="checkin-edit-end" /></label>
          <Button size="sm" onClick={save} data-testid="checkin-save">Save times</Button>
          <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
        </div>
      ) : (
        <button type="button" onClick={() => setEditing(true)} data-testid="checkin-edit" className="text-xs text-muted-foreground hover:text-brand inline-flex items-center gap-1">
          <Pencil className="w-3 h-3" /> Correct times
        </button>
      )}
    </div>
  );
}
